/**
 * BWP Laboratories - Main JavaScript
 * Handles navigation, animations, and user interactions
 */
'use strict';

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.site-header');
    this.skipLinkTarget = document.querySelector('#wp--skip-link--target');
    this.htmlElement = document.documentElement;
    this.currentScrollTop = 0;
    this.headerBounds = {};
    this.onScrollHandler = this.onScroll.bind(this);
    
    // Ensure required elements exist
    if (!this.header) {
      console.warn('StickyHeader: .site-header element not found');
    }
  }

  connectedCallback() {
    window.addEventListener('scroll', this.onScrollHandler, false);
    this.createObserver();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    if (!this.header) return;
    
    const observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    if (!this.header) return;
    
    const scrollTop = window.pageYOffset || this.htmlElement.scrollTop;
    const sectionHeader = document.querySelector('.js-section-header');
    if (!sectionHeader) return;
    
    const headerHeight = sectionHeader.offsetHeight;

    if (this.skipLinkTarget) {
      this.skipLinkTarget.classList.add('is-sticky');
    }

    if (window.scrollY > headerHeight) {
      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        requestAnimationFrame(this.reveal.bind(this));
      } else if (scrollTop <= this.headerBounds.top) {
        requestAnimationFrame(this.reset.bind(this));
      }
    } else {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (!this.header) return;
    this.header.classList.add('scrolled', 'web-section-header-sticky');
    if (this.skipLinkTarget) {
      this.skipLinkTarget.classList.add('is-sticky');
    }
    this.htmlElement.classList.add('has-sticky-header');
  }

  reveal() {
    if (!this.header) return;
    this.header.classList.add('web-section-header-sticky', 'animate', 'is-reset');
    this.header.classList.remove('scrolled');
    if (this.skipLinkTarget) {
      this.skipLinkTarget.classList.add('is-sticky');
    }
    this.htmlElement.classList.remove('has-sticky-header');
  }

  reset() {
    if (!this.header) return;
    this.header.classList.remove('is-reset');
  }
}

customElements.define('sticky-header', StickyHeader);

// ===== Mobile Menu Toggle =====
// Note: Mobile menu is handled by inline script in HTML using .js-nav-btn and .js-nav-wrap
// This code ensures nav links close the menu when clicked
document.querySelectorAll('.nav-link').forEach(link => {
  link.addEventListener('click', () => {
    const navBtn = document.querySelector('.js-nav-btn');
    const navWrap = document.querySelector('.js-nav-wrap');
    if (navBtn && navWrap && navWrap.classList.contains('is-open')) {
      navBtn.classList.remove('is-active');
      navWrap.classList.remove('is-open');
      document.documentElement.classList.remove('is-fixed', 'is-nav-open', 'has-overlay');
    }
  });
});



// ===== Smooth Scroll for Anchor Links =====
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    const targetId = this.getAttribute('href');
    // Skip empty hash or just "#"
    if (!targetId || targetId === '#') return;
    
    const target = document.querySelector(targetId);
    if (target) {
      e.preventDefault();
      const header = document.querySelector('.site-header');
      const headerHeight = header ? header.offsetHeight : 0;
      const targetPosition = target.offsetTop - headerHeight;
      window.scrollTo({
        top: Math.max(0, targetPosition), // Ensure non-negative value
        behavior: 'smooth',
      });
    }
  });
});

// ===== Image Preloading =====
/**
 * Preloads images to improve page performance and prevent layout shift
 * Images are loaded asynchronously with error handling
 */
(function preloadImages() {
  const imageUrls = [
    'assets/images/hero.jpg',
    'assets/images/who-we-are.jpg',
    'assets/images/how-we-do.jpg',
    'assets/images/who-we-work.jpg',
    'assets/images/contact-us.jpg',
  ];

  let loadedCount = 0;
  let errorCount = 0;

  const handleImageLoad = () => {
    loadedCount++;
    if (loadedCount + errorCount === imageUrls.length) {
      console.log(`Image preloading complete: ${loadedCount} loaded, ${errorCount} failed`);
    }
  };

  const handleImageError = (url) => {
    errorCount++;
    console.warn(`Failed to preload image: ${url}`);
    if (loadedCount + errorCount === imageUrls.length) {
      console.log(`Image preloading complete: ${loadedCount} loaded, ${errorCount} failed`);
    }
  };

  // Preload images with proper error handling
  imageUrls.forEach(url => {
    const img = new Image();
    img.onload = handleImageLoad;
    img.onerror = () => handleImageError(url);
    img.src = url;
  });
})();

// ===== Scroll-triggered Animations =====
/**
 * Initialize scroll animations with Intersection Observer API
 * Matches matt-seitz theme implementation
 */
(function initScrollAnimations() {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Animation class selectors
  const animationClasses = [
    'animate-fade-in',
    'animate-fade-up',
    'animate-fade-down',
    'animate-fade-left',
    'animate-fade-right',
    'animate-scale-up',
    'animate-zoom-in',
    'animate-flip-up',
    'animate-slide-up-large'
  ];
  
  // Build selector for all animation classes
  const animationSelector = animationClasses.map(cls => `.${cls}`).join(',');
  const animatedElements = document.querySelectorAll(animationSelector);
  
  if (animatedElements.length === 0) {
    return; // No animated elements found
  }
  
  if (prefersReducedMotion) {
    // If user prefers reduced motion, show all elements immediately
    animatedElements.forEach(el => {
      el.classList.add('animate-in');
    });
    return;
  }
  
  // Configuration
  const config = {
    threshold: 0.1, // Trigger when 10% of element is visible
    rootMargin: '0px 0px -50px 0px' // Trigger slightly before element enters viewport
  };
  
  // Create intersection observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        
        // Check for delay modifier class (animate-delay-100, animate-delay-200, etc.)
        let delay = 0;
        element.classList.forEach(className => {
          if (className.startsWith('animate-delay-')) {
            delay = parseInt(className.replace('animate-delay-', '')) || 0;
          }
        });
        
        // Add visible class after delay
        setTimeout(() => {
          element.classList.add('animate-in');
          
          // Optionally unobserve after animation (better performance)
          // Comment out if you want animations to trigger again when scrolling back
          observer.unobserve(element);
        }, delay);
      }
    });
  }, config);
  
  // Observe all animated elements
  animatedElements.forEach(element => {
    observer.observe(element);
  });
  
  // Handle elements already in viewport on page load
  const checkInitialVisibility = () => {
    animatedElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        element.classList.add('animate-in');
      }
    });
  };
  
  // Check initial visibility after a short delay
  setTimeout(checkInitialVisibility, 100);
})();
